import { AutocompleteElement } from "@nosto/search-js/preact/autocomplete"
import type { Product } from "@/types"
import DynamicCard from "@/elements/DynamicCard/DynamicCard"
import styles from "./DynamicCardProduct.module.css"
import { getVariantId } from "@/utils/getVariantId"

type Props = {
  hit: Product
}

/**
 * Renders an autocomplete product suggestion as a Shopify section-rendered
 * Dickies clean card via `<nosto-dynamic-card section="nosto-dickies-product-card-clean">`.
 *
 * Click tracking is preserved by wrapping the dynamic card in `AutocompleteElement`,
 * which forwards the click event from the host element to Nosto analytics. The
 * theme-side `nosto-dickies-product-card-clean` section renders the existing
 * `dickies-product-card` snippet with `layout: 'clean'`, matching the current
 * search-drawer suggestion card.
 */
export default function DynamicCardProduct({ hit }: Props) {
  if (!hit.handle || !hit.url) {
    return null
  }

  const url = hit.url
  const variantId = getVariantId(hit)

  return (
    <AutocompleteElement
      key={hit.productId}
      as={DynamicCard}
      hit={{
        productId: hit.productId!,
        url
      }}
      componentProps={{
        handle: hit.handle,
        section: "nosto-dickies-product-card-clean",
        placeholder: true,
        className: styles.container,
        "aria-label": `Product ${hit.name}`,
        ...(variantId ? { "variant-id": variantId } : {}),
        /**
         * `useRovingFocus` activates focused `.ns-autocomplete-element` rows via `.click()`.
         * `<nosto-dynamic-card>` has no default navigation; wiring host click ensures keyboard Enter
         * reaches the storefront PDP while `AutocompleteElement` still records the Nosto hit click first.
         */
        onClick: () => {
          window.location.assign(url)
        }
      }}
    />
  )
}
